import { AppBlock } from '@boost/widget-integration-tae/lib';
import { Module, ModuleRefImpl } from 'framework/core';

import { DomHelper } from '../../helpers/dom.helper';
import { AppService } from '../app/app.service';
import { B2BModule } from '../b2b/b2b.module';
import { PlatformModule } from '../platform/platform.module';
import { TemplateModule } from '../template/template.module';
import { FilterAPI } from './filter.api';
import { FilterController } from './filter.controller';
import { FilterFormatHelper } from './filter.format.helper';
import { FilterHandler } from './filter.handler';
import { FilterHelper } from './filter.helper';
import { FilterRender } from './filter.render';
import { FilterService } from './filter.service';
import { FilterStickyController } from './filter.sticky.controller';
import { FilterStorageHelper } from './filter.storage.helper';
import { FilterStore } from './filter.store';
import { FilterTranslationService } from './filter.translation.service';
import { FilterUrlHelper } from './filter.url.helper';
import { FilterValidationHelper } from './filter.validation.helper';
/**
 * Filter widget module for Widget Integration.
 *
 * Manages the initialization, registration, and lifecycle of filter widgets on collection
 * and search pages. Handles widget connection with TAE framework and dependency injection
 * for all filter-related services.
 *
 * @remarks
 * This module is registered with the TAE framework and automatically initializes filter
 * widgets when filter blocks are detected. It provides all filter services, helpers, and
 * controllers through dependency injection.
 *
 * Technical Support teams typically don't extend this module directly. Instead, extend
 * the individual services (FilterAPI, FilterHandler, FilterHelper) exported by this module.
 *
 * @example
 * Access filter services in custom code:
 * ```javascript
 * // Get FilterAPI instance from module
 * const filterAPI = moduleRef.get('FilterAPI');
 *
 * // Extend FilterAPI for shop-specific customization
 * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
 *   return class CustomFilterAPI extends FilterAPI {
 *     // Custom implementation
 *   };
 * });
 * ```
 */
@Module({
  imports: [TemplateModule, PlatformModule, B2BModule],
  providers: [
    DomHelper,
    // New modular providers
    FilterController,
    FilterStickyController,
    FilterStore,
    FilterRender,
    FilterHelper,
    FilterAPI,
    FilterService,
    FilterHandler,
    FilterTranslationService,
    // New helper classes
    FilterUrlHelper,
    FilterFormatHelper,
    FilterStorageHelper,
    FilterValidationHelper,
  ],
  exports: [
    FilterController,
    FilterStickyController,
    FilterStore,
    FilterRender,
    FilterHelper,
    FilterAPI,
    FilterService,
    FilterUrlHelper,
    FilterFormatHelper,
    FilterStorageHelper,
    FilterValidationHelper,
  ],
})
export class FilterModule {
  /**
   * Creates a new FilterModule instance.
   *
   * Initializes the filter module and sets up block listeners to automatically
   * detect and initialize filter widgets when they are registered with TAEApp.
   *
   * @param moduleRef - Module reference for resolving dependencies
   * @param appService - Application service for accessing TAEApp instance
   */
  constructor(
    protected moduleRef: ModuleRefImpl,
    protected appService: AppService
  ) {
    this.setupBlockListener();
  }

  /**
   * Sets up listener for filter block registration with TAEApp.
   *
   * Registers a callback that triggers when TAEApp detects a new block.
   * Filters for blocks with blockType 'filter' and initializes them.
   *
   * @remarks
   * This method is called during module construction and sets up the
   * automatic widget initialization system. Override this in extended
   * modules to customize block detection logic.
   */
  protected setupBlockListener(): void {
    this.appService.TAEApp.addRegistryBlockListener((block) => {
      if (block.blockType === 'filter') {
        this.onInitBlock(block);
      }
    });
  }
  /**
   * Initializes a filter widget block.
   *
   * Called when a new filter block is registered with TAEApp. Validates the block
   * has a valid DOM context, resolves the FilterController from dependency injection,
   * and connects the widget with the block configuration.
   *
   * @param block - The AppBlock instance to initialize as a filter widget
   *
   * @remarks
   * This method includes error handling that logs initialization failures without
   * breaking the application. Override this to customize initialization logic or
   * add shop-specific validation.
   *
   * @example
   * Override to add custom block validation:
   * ```typescript
   * protected async onInitBlock(block: AppBlock): Promise<void> {
   *   // Custom validation
   *   if (!this.isValidFilterBlock(block)) {
   *     console.warn('Skipping invalid filter block:', block.id);
   *     return;
   *   }
   *   return super.onInitBlock(block);
   * }
   * ```
   */
  protected async onInitBlock(block: AppBlock): Promise<void> {
    try {
      if (block.document instanceof HTMLElement) {
        const widget: FilterController = await this.moduleRef.resolve(FilterController);
        await this.connectWidget(widget, block);
      }
    } catch (error) {
      this.handleBlockInitError(error as Error, block);
    }
  }
  /**
   * Connects a FilterController widget with an AppBlock configuration.
   *
   * Extracts filter-specific properties from the block and passes them to the
   * widget's connect method. This establishes the widget's initial state and
   * configuration.
   *
   * @param widget - The FilterController instance to connect
   * @param block - The AppBlock containing filter configuration data
   *
   * @remarks
   * The block is cast to access filter-specific properties (filterTree, cache, etc.)
   * that are not part of the base AppBlock type. Override this method to customize
   * which properties are passed to the widget or to add shop-specific configuration.
   *
   * @example
   * Override to add custom widget configuration:
   * ```typescript
   * protected async connectWidget(
   *   widget: FilterController,
   *   block: AppBlock
   * ): Promise<void> {
   *   await super.connectWidget(widget, block);
   *   // Add custom post-connection logic
   *   widget.setCustomSettings(this.getShopSettings());
   * }
   * ```
   */
  protected async connectWidget(widget: FilterController, block: AppBlock): Promise<void> {
    // Cast to access filter-specific properties not in base AppBlock type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filterBlock = block as any;

    widget.connect({
      document: filterBlock.document,
      cache: filterBlock.cache,
      defaultSort: filterBlock.defaultSort,
      defaultParams: filterBlock.defaultParams,
      widgetId: filterBlock.widgetId,
      dynamicBundles: filterBlock.dynamicBundles,
      filter: filterBlock.filterTree,
      id: filterBlock.id,
      blockType: filterBlock.blockType,
      templateSettings: filterBlock.widgetInfo,
    });
  }
  /**
   * Handles errors during filter block initialization.
   *
   * Logs initialization errors to the console without throwing, preventing
   * one failed widget from breaking other widgets or the application.
   *
   * @param error - The error that occurred during initialization
   * @param block - The AppBlock that failed to initialize
   *
   * @remarks
   * Override this method to add custom error handling, error reporting to
   * analytics services, or shop-specific error recovery logic.
   *
   * @example
   * Override to add error tracking:
   * ```typescript
   * protected handleBlockInitError(error: Error, block: AppBlock): void {
   *   super.handleBlockInitError(error, block);
   *   // Send to error tracking service
   *   this.errorTracker.captureException(error, {
   *     context: 'filter-widget-init',
   *     blockId: block.id,
   *     blockType: block.blockType
   *   });
   * }
   * ```
   */
  protected handleBlockInitError(error: Error, block: AppBlock): void {
    console.error(`Failed to initialize filter widget for block ${block.id}:`, error);
  }
}
